import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav.jsx';
import PublicFooter from '../components/PublicFooter.jsx';
import PageMeta from '../components/PageMeta.jsx';
import { useTranslation } from '../i18n/I18nContext.jsx';
import { useLocalizedPath } from '../hooks/useLocalizedPath.js';

function Section({ title, children }) {
  return (
    <div className="legal-section">
      <h2 className="legal-section-title">{title}</h2>
      {children}
    </div>
  );
}

export default function PrivacyPage() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();

  return (
    <>
      <PageMeta
        title={t('privacy.meta.title')}
        description={t('privacy.meta.description')}
        path="/privacy"
      />

      <div className="pub-page">
        <PublicNav />

        <main id="main-content" className="legal-page">
          <div className="legal-header">
            <h1 className="legal-title">{t('privacy.header.title')}</h1>
            <p className="legal-date">{t('privacy.header.lastUpdated')}</p>
            <p className="legal-intro">{t('privacy.header.intro')}</p>
          </div>

          <div className="legal-body">
            <Section title={t('privacy.s1.title')}>
              <p>
                {t('privacy.s1.bodyPre')}{' '}
                <a href="mailto:info@claw-dius.com">info@claw-dius.com</a>
                {t('privacy.s1.bodyPost')}
              </p>
            </Section>

            <Section title={t('privacy.s2.title')}>
              <p>{t('privacy.s2.intro')}</p>
              <ul>
                <li><strong>{t('privacy.s2.item1label')}</strong> {t('privacy.s2.item1text')}</li>
                <li><strong>{t('privacy.s2.item2label')}</strong> {t('privacy.s2.item2text')}</li>
                <li><strong>{t('privacy.s2.item3label')}</strong> {t('privacy.s2.item3text')}</li>
                <li><strong>{t('privacy.s2.item4label')}</strong> {t('privacy.s2.item4text')}</li>
                <li><strong>{t('privacy.s2.item5label')}</strong> {t('privacy.s2.item5text')}</li>
                <li><strong>{t('privacy.s2.item6label')}</strong> {t('privacy.s2.item6text')}</li>
              </ul>
            </Section>

            <Section title={t('privacy.s3.title')}>
              <p>{t('privacy.s3.intro')}</p>
              <ul>
                <li>{t('privacy.s3.item1')}</li>
                <li>{t('privacy.s3.item2')}</li>
                <li>{t('privacy.s3.item3')}</li>
                <li>{t('privacy.s3.item4')}</li>
                <li>{t('privacy.s3.item5')}</li>
              </ul>
              <p>{t('privacy.s3.outro')}</p>
            </Section>

            <Section title={t('privacy.s4.title')}>
              <ul>
                <li>{t('privacy.s4.item1')}</li>
                <li>{t('privacy.s4.item2')}</li>
                <li>{t('privacy.s4.item3')}</li>
                <li>{t('privacy.s4.item4')}</li>
                <li>
                  {t('privacy.s4.item5pre')}{' '}
                  <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">Stripe</a>
                  {t('privacy.s4.item5post')}
                </li>
              </ul>
            </Section>

            <Section title={t('privacy.s5.title')}>
              <ul>
                <li>{t('privacy.s5.item1')}</li>
                <li>{t('privacy.s5.item2')}</li>
                <li>{t('privacy.s5.item3')}</li>
                <li>{t('privacy.s5.item4')}</li>
                <li>{t('privacy.s5.item5')}</li>
              </ul>
            </Section>

            <Section title={t('privacy.s6.title')}>
              <p>{t('privacy.s6.intro')}</p>
              <ul>
                <li><strong>{t('privacy.s6.item1label')}</strong> {t('privacy.s6.item1text')}</li>
                <li><strong>{t('privacy.s6.item2label')}</strong> {t('privacy.s6.item2text')}</li>
                <li><strong>{t('privacy.s6.item3label')}</strong> {t('privacy.s6.item3text')}</li>
              </ul>
            </Section>

            <Section title={t('privacy.s7.title')}>
              <p>{t('privacy.s7.intro')}</p>
              <ul>
                <li><strong>{t('privacy.s7.item1label')}</strong> {t('privacy.s7.item1text')}</li>
                <li><strong>{t('privacy.s7.item2label')}</strong> {t('privacy.s7.item2text')}</li>
                <li><strong>{t('privacy.s7.item3label')}</strong> {t('privacy.s7.item3text')}</li>
                <li><strong>{t('privacy.s7.item4label')}</strong> {t('privacy.s7.item4text')}</li>
                <li><strong>{t('privacy.s7.item5label')}</strong> {t('privacy.s7.item5text')}</li>
              </ul>
              <p>
                {t('privacy.s7.outropre')}{' '}
                <a href="mailto:privacy@claw-dius.com">privacy@claw-dius.com</a>.{' '}
                {t('privacy.s7.outropost')}
              </p>
            </Section>

            <Section title={t('privacy.s8.title')}>
              <p>{t('privacy.s8.body1')}</p>
              <p>
                {t('privacy.s8.body2pre')}{' '}
                <a href="mailto:privacy@claw-dius.com">privacy@claw-dius.com</a>.
              </p>
            </Section>

            <Section title={t('privacy.s9.title')}>
              <p>{t('privacy.s9.body')}</p>
            </Section>

            <Section title={t('privacy.s10.title')}>
              <p>{t('privacy.s10.body')}</p>
            </Section>

            <Section title={t('privacy.s11.title')}>
              <p>{t('privacy.s11.intro')}</p>
              <ul>
                <li>{t('privacy.s11.privacyEmailLabel')}: <a href="mailto:privacy@claw-dius.com">privacy@claw-dius.com</a></li>
                <li>{t('privacy.s11.generalEmailLabel')}: <a href="mailto:info@claw-dius.com">info@claw-dius.com</a></li>
                <li>{t('privacy.s11.contactFormPre')} <Link to={lp('/contact')}>{t('privacy.s11.contactFormLink')}</Link></li>
              </ul>
            </Section>
          </div>

          <nav className="legal-nav" aria-label={t('privacy.nav.ariaLabel')}>
            <Link to={lp('/')}>{t('privacy.nav.backHome')}</Link>
            <Link to={lp('/terms')}>{t('privacy.nav.terms')}</Link>
            <Link to={lp('/contact')}>{t('privacy.nav.contact')}</Link>
          </nav>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}
